// Every persisted literal is pinned in value-fidelity.test.ts. Numbers are
// plain floats, not int64, so these shapes pass through getSession unconverted.

import type { AssessmentMarkResult, StoredItemResult } from "./assessment-sync";
import { OrientationPhase } from "./study";
import { extractNormalizedWord } from "./word";

export const READER_CONTENT_STATUSES = [
  "selecting",
  "loading",
  "error",
  "ready",
] as const;

export type ReaderContentStatus = (typeof READER_CONTENT_STATUSES)[number];

export const ASSESSMENT_ITEM_RESULTS = [
  "correct",
  "incorrect",
  "skipped",
] as const satisfies readonly AssessmentMarkResult[];

// Persisted with the phase completion because the live
// orientationState.itemResults is cleared at completion.
export interface PersistedAssessmentItemResult {
  index: number;
  item?: string;
  result: AssessmentMarkResult;
  markedAt: number;
}

// Only "norms" is a real norms percentile; with any other status the stored
// number is a bound or 0.
export const WRF_PERCENTILE_STATUSES = [
  "norms",
  "above_norms",
  "below_norms",
  "no_norms",
  "lookup_failed",
] as const;

export type WrfPercentileStatus = (typeof WRF_PERCENTILE_STATUSES)[number];

// Only the last segment is scored; earlier ones are research data only.
export interface AwrfSegmentResultRecord {
  grade: number;
  gradePeriod: string;
  segmentStart: number;
  scoringBoundary: number;
  rawScore: number;
  percentile: number;
  // Absent on records written before it existed, which mean "norms".
  percentileStatus?: WrfPercentileStatus;
  // The values at the 60 s mark that triggered a downgrade; rawScore and
  // percentile above are recomputed from the reviewed marks.
  decisionRawScore?: number;
  decisionPercentile?: number;
  decisionPercentileStatus?: WrfPercentileStatus;
}

export const AWRF_TIMED_SEGMENT_OUTCOMES = ["downgrade", "final"] as const;

export type AwrfTimedSegmentOutcome =
  (typeof AWRF_TIMED_SEGMENT_OUTCOMES)[number];

// Written in the same mutation that edits the word list, so Convex always holds
// a consistent run for an aide reload to rebuild from. The scored window is
// [segmentStart, scoringBoundary), scoringBoundary = active index at 60 s + 1.
export interface AwrfTimedSegmentRecord {
  outcome: AwrfTimedSegmentOutcome;
  grade: string;
  segmentStart: number;
  scoringBoundary: number;
  rawScore: number;
  gradePeriod: string;
  percentile?: number;
  percentileStatus: WrfPercentileStatus;
  percentileBound?: number;
  runwayEnd?: number;
  /** Decided at the first-line discontinue rule, not at 60 s. */
  discontinued?: true;
  decidedAt: number;
}

export interface AwrfSegmentDecision {
  segmentStart: number;
  scoringBoundary: number;
  rawScore: number;
  gradePeriod: string;
  percentile?: number;
  percentileStatus: WrfPercentileStatus;
  percentileBound?: number;
}

export interface LnfPreviousAttempt {
  score: number;
  completedAt: number;
  elapsedMs?: number;
  progressIndexAt60s?: number;
  formId?: string;
  itemResults?: PersistedAssessmentItemResult[];
  itemNotes?: { index: number; note: string }[];
  assessmentNote?: string;
}

// Also the aWRF hesitation delay: when it elapses the word's audio is supplied,
// so a hesitated word was not named in time whatever the aide marks.
export const AWRF_FLUENT_WITHIN_MS = 3000;

export const ASSESSMENT_KINDS = [
  OrientationPhase.Lnf,
  OrientationPhase.Awrf,
  OrientationPhase.Orf,
] as const;

export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number];

// orientationWordInteractions.developingReason: the mark an aide puts on an
// exploration or practice word.
export const DEVELOPING_REASONS = [
  "guessed",
  "incorrect",
  "skipped",
  "struggled",
] as const;
export type DevelopingReason = (typeof DEVELOPING_REASONS)[number];

// orientationSessions.readerSource. There is no "typed" member, so a session
// for a reader id an aide typed has no way to be stored.
export const READER_SOURCES = ["bookmark", "scheduled_call"] as const;
export type ReaderSource = (typeof READER_SOURCES)[number];

// phases.deviceSetup.linkSendStatus, the outcome of the latest setup-link
// send. A not_deliverable or provider_error refusal is recorded as "failed".
export const LINK_SEND_STATUSES = [
  "sent",
  "failed",
  "rate_limited",
  "opted_out",
  "unconfigured",
] as const;
export type LinkSendStatus = (typeof LINK_SEND_STATUSES)[number];

// One state machine for every timed attempt of every instrument. A retry is a
// new run, so "abandoned" keeps the earlier attempt rather than erasing it.
export const ORIENTATION_RUN_STATUSES = [
  "practice",
  "ready",
  "timing",
  "paused",
  "review",
  "adaptive",
  "completed",
  "abandoned",
] as const;

export type OrientationRunStatus = (typeof ORIENTATION_RUN_STATUSES)[number];

// Server truth from Zoom's REST responses and webhooks, never from a browser.
export const RECORDING_STATUSES = [
  "starting",
  "recording",
  "paused",
  "stopped",
  "stopped_unconsented",
  "deleted",
] as const;

export type RecordingStatus = (typeof RECORDING_STATUSES)[number];

export const AUDIO_SUPPLY_CAUSES = ["hesitation", "aide"] as const;

export type AudioSupplyCause = (typeof AUDIO_SUPPLY_CAUSES)[number];

export interface RunItemTiming {
  index: number;
  displayedAt?: number;
  hesitatedAt?: number;
  audioSuppliedAt?: number;
  audioSuppliedBy?: AudioSupplyCause;
  autoMarkedAt?: number;
}

// A stretch of a timed minute the aide's browser was disconnected for, kept
// so research can exclude a minute nobody watched. No `to` while still away.
export interface AideGap {
  from: number;
  to?: number;
}

// The latest markedAt wins per index; on a tie the later entry wins.
export function latestMarkByIndex(
  marks: readonly StoredItemResult[],
): Map<number, StoredItemResult> {
  const latest = new Map<number, StoredItemResult>();
  for (const mark of marks) {
    const prior = latest.get(mark.index);
    if (!prior || mark.markedAt >= prior.markedAt) latest.set(mark.index, mark);
  }
  return latest;
}

// A word counts as hesitated if the 3 s timer fired, or if its audio was
// supplied before its latest correct mark whoever played it: a word the child
// heard first was not named unaided. Feeds both isAboveCeiling and the
// Recognition/Identification split of the server-written aWRF responses.
export function hesitatedIndicesOf({
  marks,
  items,
}: {
  marks: readonly StoredItemResult[];
  items: readonly RunItemTiming[];
}): Set<number> {
  const latest = latestMarkByIndex(marks);
  const hesitated = new Set<number>();
  for (const { index, hesitatedAt, audioSuppliedAt } of items) {
    const mark = latest.get(index);
    const heardBeforeNamed =
      audioSuppliedAt !== undefined &&
      mark?.result === "correct" &&
      audioSuppliedAt < mark.markedAt;
    if (hesitatedAt !== undefined || heardBeforeNamed) hesitated.add(index);
  }
  return hesitated;
}

// Each word the server marked incorrect when it supplied the word in the aWRF
// minute, and whether the aide overrode that mark: true once the latest mark
// is anything but incorrect, a withdrawn mark included.
export function autoMarkOverriddenOf({
  marks,
  items,
}: {
  marks: readonly StoredItemResult[];
  items: readonly RunItemTiming[];
}): Map<number, boolean> {
  const latest = latestMarkByIndex(marks);
  const overridden = new Map<number, boolean>();
  for (const { index, autoMarkedAt } of items) {
    if (autoMarkedAt === undefined) continue;
    overridden.set(index, latest.get(index)?.result !== "incorrect");
  }
  return overridden;
}

// The study's only ceiling, and exactly what `above_ceiling` means in ./study:
// every word of the whole aWRF list (adaptive words included) marked
// "correct", none hesitated. No score threshold enters into it. An unmarked
// word means aWRF was not completed.
// Item results carry no timing, so a caller that can't say which words
// hesitated can't decide the ceiling.
export function isAboveCeiling({
  wordCount,
  itemResults,
  hesitatedIndices,
}: {
  wordCount: number;
  itemResults: readonly StoredItemResult[];
  hesitatedIndices: Iterable<number>;
}): boolean {
  if (!Number.isInteger(wordCount) || wordCount <= 0) return false;
  for (const index of hesitatedIndices) {
    if (index >= 0 && index < wordCount) return false;
  }
  const inList = itemResults.filter(
    ({ index }) => Number.isInteger(index) && index >= 0 && index < wordCount,
  );
  const latest = latestMarkByIndex(inList);
  if (latest.size !== wordCount) return false;
  for (const { result } of latest.values()) {
    if (result !== "correct") return false;
  }
  return true;
}

type LiveItemResult = Omit<PersistedAssessmentItemResult, "item">;

export function resolveItemResults(
  clientItemResults: readonly PersistedAssessmentItemResult[] | undefined,
  serverItemResults: readonly LiveItemResult[] | undefined,
): PersistedAssessmentItemResult[] | undefined {
  if (clientItemResults && clientItemResults.length > 0) {
    return [...clientItemResults];
  }
  if (serverItemResults && serverItemResults.length > 0) {
    return serverItemResults.map(({ index, result, markedAt }) => ({
      index,
      result,
      markedAt,
    }));
  }
  return undefined;
}

// The only case where an already recorded phase may be overwritten.
export function isRetryableLnfCompletion(
  sessionPhase: string,
  existingLnf: { passed: boolean; skippedReason?: string } | undefined,
  phaseData: { phase: string; score?: number; skippedReason?: string },
): boolean {
  return (
    phaseData.phase === "lnf" &&
    sessionPhase === "lnf" &&
    existingLnf !== undefined &&
    !existingLnf.passed &&
    existingLnf.skippedReason === undefined &&
    phaseData.skippedReason === undefined &&
    phaseData.score !== undefined
  );
}

// A failed, scored LNF is kept: the next completion is a retry that archives it
// with its previousAttempts, so deleting it would lose every earlier attempt.
export function restartPhaseKeepsRecord(
  phase: string,
  existing: { passed?: boolean; skippedReason?: string } | undefined,
): boolean {
  return (
    phase === "lnf" &&
    existing !== undefined &&
    existing.passed === false &&
    existing.skippedReason === undefined
  );
}

export function getPhaseCompletionError({
  sessionPhase,
  phaseName,
  alreadyRecorded,
  isLnfRetry,
}: {
  sessionPhase: string;
  phaseName: string;
  alreadyRecorded: boolean;
  isLnfRetry: boolean;
}): string | null {
  if (alreadyRecorded && !isLnfRetry) {
    return `Phase already completed: ${phaseName} (use Reset Session to retry)`;
  }
  if (phaseName !== sessionPhase) {
    return `Phase mismatch: cannot complete ${phaseName} while session is in ${sessionPhase}`;
  }
  return null;
}

export const WORDS_READ_PHASES = ["exploration", "practice"] as const;

export type WordsReadPhase = (typeof WORDS_READ_PHASES)[number];

// Convex arrays max out at 8192 elements.
export const ORIENTATION_UNIQUE_WORDS_CAP = 5000;
export const ORIENTATION_READ_PAGE_KEYS_CAP = 4000;
// Guards against a soft-auth caller.
export const ORIENTATION_MAX_WORDS_PER_PAGE = 2000;

export function wordsReadPageKey(
  phase: WordsReadPhase,
  readingId: string,
  pageIndex: number,
): string {
  return `${phase}:${readingId}:${pageIndex}`;
}

export interface WordsReadTally {
  totalWords: number;
  uniqueWords: string[];
  pageKeys: string[];
}

// "Words read" (owner ruling): every word occurrence on a page the reader
// completed (moved forward from, or finished the reading on), once per
// (phase, reading, page). The unique set is shared across both phases.
export function applyWordsRead(
  prev: {
    totalWords: number;
    uniqueWords: readonly string[];
    pageKeys: readonly string[];
  },
  page: { pageKey: string; words: readonly string[] },
): WordsReadTally | null {
  if (prev.pageKeys.includes(page.pageKey)) return null;

  const normalized = page.words
    .slice(0, ORIENTATION_MAX_WORDS_PER_PAGE)
    .map((w) => extractNormalizedWord(w))
    .filter((w) => w.length > 0);

  const unique = new Set(prev.uniqueWords);
  for (const word of normalized) {
    if (unique.size >= ORIENTATION_UNIQUE_WORDS_CAP) break;
    unique.add(word);
  }

  const pageKeys = [...prev.pageKeys, page.pageKey];
  return {
    totalWords: prev.totalWords + normalized.length,
    uniqueWords: [...unique],
    pageKeys:
      pageKeys.length > ORIENTATION_READ_PAGE_KEYS_CAP
        ? pageKeys.slice(-ORIENTATION_READ_PAGE_KEYS_CAP)
        : pageKeys,
  };
}

export interface WordsReadState {
  explorationTotalWords?: number;
  practiceTotalWords?: number;
  uniqueWordsRead?: string[];
  wordsReadPageKeys?: string[];
  currentPageRead?: CurrentPageRead;
}

export interface CurrentPageRead {
  phase: WordsReadPhase;
  readingId: string;
  pageIndex: number;
  words: string[];
}

export interface WordsReadStatePatch {
  explorationTotalWords?: number;
  practiceTotalWords?: number;
  uniqueWordsRead?: string[];
  wordsReadPageKeys?: string[];
}

export function wordsReadPatch(
  state: WordsReadState,
  phase: WordsReadPhase,
  page: { readingId: string; pageIndex: number; words: readonly string[] },
): { patch: WordsReadStatePatch; totalWords: number } {
  const prevTotal =
    (phase === "exploration"
      ? state.explorationTotalWords
      : state.practiceTotalWords) ?? 0;
  const next = applyWordsRead(
    {
      totalWords: prevTotal,
      uniqueWords: state.uniqueWordsRead ?? [],
      pageKeys: state.wordsReadPageKeys ?? [],
    },
    {
      pageKey: wordsReadPageKey(phase, page.readingId, page.pageIndex),
      words: page.words,
    },
  );
  if (!next) return { patch: {}, totalWords: prevTotal };
  return {
    patch: {
      ...(phase === "exploration"
        ? { explorationTotalWords: next.totalWords }
        : { practiceTotalWords: next.totalWords }),
      uniqueWordsRead: next.uniqueWords,
      wordsReadPageKeys: next.pageKeys,
    },
    totalWords: next.totalWords,
  };
}

// The page on screen when a phase ends never completes the normal way (practice
// auto-completes on the 10th match, or the aide moves on), so the phase-ending
// mutations count it here.
export function pendingPageReadPatch(
  state: WordsReadState,
  leavingPhase: string,
): (WordsReadStatePatch & { currentPageRead: undefined }) | null {
  const pending = state.currentPageRead;
  if (!pending || pending.phase !== leavingPhase) return null;
  const { patch } = wordsReadPatch(state, pending.phase, pending);
  return { ...patch, currentPageRead: undefined };
}

// Keyed on the word occurrence, not responseType: one occurrence counts once
// however the child matched it. Aide right-click marks never count as a match.
export function isMatchCompletionReplay(
  row: {
    readingId?: string;
    wordIndex?: number;
    developingReason?: string;
  },
  match: { readingId?: string; wordIndex: number },
): boolean {
  return (
    row.developingReason === undefined &&
    row.readingId === match.readingId &&
    row.wordIndex === match.wordIndex
  );
}

interface ReaderHighlightSnapshot {
  focusWordIndex?: number;
  isDrawerOpen?: boolean;
  currentPage?: number;
  readingId?: string;
}

export function shouldClearAideHighlight(
  highlightedWordIndex: number | undefined,
  prev: ReaderHighlightSnapshot,
  next: ReaderHighlightSnapshot,
): boolean {
  if (highlightedWordIndex === undefined) return false;
  if (next.currentPage !== prev.currentPage) return true;
  if (next.readingId !== prev.readingId) return true;
  if (next.focusWordIndex !== highlightedWordIndex) return false;
  const focusMovedOnto = prev.focusWordIndex !== highlightedWordIndex;
  const drawerOpened = next.isDrawerOpen === true && prev.isDrawerOpen !== true;
  return focusMovedOnto || drawerOpened;
}

// Idempotent on a double click; a normally completed session is never
// relabelled as an early exit.
export function getEarlyExitAction(session: {
  phase: string;
  earlyExitAt?: number;
}): "already_exited" | "already_complete" | "exit" {
  if (session.earlyExitAt !== undefined) return "already_exited";
  if (session.phase === "complete") return "already_complete";
  return "exit";
}
